import { supabase } from '../lib/supabase';

// Função para criar usuário administrativo sem deslogar o usuário atual
export const createAdminUser = async (userData) => {
  try {
    console.log('🔄 Criando usuário administrativo:', userData.email);
    
    // Salvar sessão atual para restaurar depois
    const { data: currentSession } = await supabase.auth.getSession();
    
    // Usar signUp normal (mesmo método dos alunos)
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email: userData.email,
      password: userData.password,
      options: {
        data: {
          name: userData.name,
          role: 'admin'
        }
      }
    });

    if (authError) {
      console.error('❌ Erro ao criar usuário no auth:', authError);
      throw new Error(authError.message);
    }

    if (!authData.user) {
      throw new Error('Usuário não foi criado no sistema de autenticação');
    }

    console.log('✅ Usuário criado no auth:', authData.user.id);

    // Criar perfil na tabela profiles
    const profileData = {
      id: authData.user.id,
      email: userData.email,
      name: userData.name,
      phone: userData.phone || '',
      department: userData.department || 'Administrativo',
      role: 'admin',
      school_adm: false,  // Super admin por padrão
      avatar_url: userData.avatar_url || '',
      is_active: userData.is_active !== undefined ? userData.is_active : true
    };

    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .insert(profileData)
      .select()
      .single();

    if (profileError) {
      console.error('❌ Erro ao criar perfil:', profileError);
      throw new Error(profileError.message);
    }

    console.log('✅ Perfil criado com sucesso:', profile.id);

    // Restaurar sessão original para não deslogar o usuário atual
    if (currentSession?.session) {
      console.log('🔄 Restaurando sessão original...');
      await supabase.auth.setSession(currentSession.session);
      console.log('✅ Sessão original restaurada');
    } else {
      // Se não havia sessão, fazer logout do novo usuário criado
      console.log('🚪 Fazendo logout do usuário recém-criado...');
      await supabase.auth.signOut();
    }
    
    return {
      success: true,
      data: {
        ...profile,
        auth_user_id: authData.user.id
      }
    };
  } catch (error) {
    console.error('❌ Erro geral ao criar usuário administrativo:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Buscar todos os usuários administrativos
export const getAllAdminUsers = async () => {
  try {
    console.log('🔄 Buscando usuários administrativos...');
    
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('role', 'admin')
      .eq('school_adm', false)  // Filtrar apenas super admins
      .order('created_at', { ascending: false });

    if (error) {
      console.error('❌ Erro ao buscar usuários:', error);
      throw new Error(error.message);
    }

    console.log('✅ Usuários encontrados:', data?.length || 0);
    return { success: true, data: data || [] };
  } catch (error) {
    console.error('❌ Erro geral ao buscar usuários:', error);
    return { success: false, error: error.message };
  }
};

// Buscar usuário por ID
export const getAdminUserById = async (id) => {
  try {
    console.log('🔄 Buscando usuário por ID:', id);
    
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', id)
      .eq('role', 'admin')
      .eq('school_adm', false)  // Filtrar apenas super admins
      .single();

    if (error) {
      console.error('❌ Erro ao buscar usuário:', error);
      throw new Error(error.message);
    }

    console.log('✅ Usuário encontrado:', data.email);
    return { success: true, data };
  } catch (error) {
    console.error('❌ Erro geral ao buscar usuário:', error);
    return { success: false, error: error.message };
  }
};

// Atualizar usuário administrativo
export const updateAdminUser = async (id, userData) => {
  try {
    console.log('🔄 Atualizando usuário:', id);
    
    const updateData = {
      name: userData.name,
      phone: userData.phone || '',
      department: userData.department || 'Administrativo',
      avatar_url: userData.avatar_url || '',
      is_active: userData.is_active !== undefined ? userData.is_active : true,
      updated_at: new Date().toISOString()
    };

    const { data, error } = await supabase
      .from('profiles')
      .update(updateData)
      .eq('id', id)
      .eq('role', 'admin')
      .eq('school_adm', false)  // Filtrar apenas super admins
      .select()
      .single();

    if (error) {
      console.error('❌ Erro ao atualizar usuário:', error);
      throw new Error(error.message);
    }

    console.log('✅ Usuário atualizado:', data.email);
    return { success: true, data };
  } catch (error) {
    console.error('❌ Erro geral ao atualizar usuário:', error);
    return { success: false, error: error.message };
  }
};

// Alternar status ativo/inativo
export const toggleAdminUserStatus = async (id, isActive) => {
  try {
    console.log('🔄 Alterando status do usuário:', id, 'para', isActive ? 'ativo' : 'inativo');
    
    const { data, error } = await supabase
      .from('profiles')
      .update({ 
        is_active: isActive,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .eq('role', 'admin')
      .eq('school_adm', false)  // Filtrar apenas super admins
      .select()
      .single();

    if (error) {
      console.error('❌ Erro ao alterar status:', error);
      throw new Error(error.message);
    }

    console.log('✅ Status alterado:', data.email, '-', isActive ? 'ativo' : 'inativo');
    return { success: true, data };
  } catch (error) {
    console.error('❌ Erro geral ao alterar status:', error);
    return { success: false, error: error.message };
  }
};

// Deletar usuário administrativo
export const deleteAdminUser = async (id) => {
  try {
    console.log('🔄 Deletando usuário:', id);
    
    // Primeiro buscar o usuário para confirmar que existe
    const { data: user, error: fetchError } = await supabase
      .from('profiles')
      .select('email')
      .eq('id', id)
      .eq('role', 'admin')
      .eq('school_adm', false)  // Filtrar apenas super admins
      .single();

    if (fetchError) {
      console.error('❌ Usuário não encontrado:', fetchError);
      throw new Error('Usuário não encontrado');
    }

    // Deletar apenas da tabela profiles (não podemos deletar do auth sem Admin API)
    const { error: deleteError } = await supabase
      .from('profiles')
      .delete()
      .eq('id', id)
      .eq('role', 'admin')
      .eq('school_adm', false);  // Filtrar apenas super admins
    
    if (deleteError) {
      console.error('❌ Erro ao deletar usuário:', deleteError);
      throw new Error(deleteError.message);
    }

    console.log('✅ Usuário removido da tabela profiles:', user.email);
    console.log('⚠️ Nota: O usuário ainda existe no sistema de autenticação');
    return { success: true };
  } catch (error) {
    console.error('❌ Erro geral ao deletar usuário:', error);
    return { success: false, error: error.message };
  }
};

// Buscar com filtros
export const searchAdminUsers = async (searchTerm = '', filters = {}) => {
  try {
    console.log('🔍 Buscando usuários com filtros:', { searchTerm, filters });
    
    let query = supabase
      .from('profiles')
      .select('*')
      .eq('role', 'admin')
      .eq('school_adm', false);  // Filtrar apenas super admins

    // Filtro de busca por nome ou email
    if (searchTerm) {
      query = query.or(`name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`);
    }

    // Filtro por departamento
    if (filters.department) {
      query = query.eq('department', filters.department);
    }

    // Filtro por status
    if (filters.is_active !== undefined) {
      query = query.eq('is_active', filters.is_active);
    }

    // Ordenação
    query = query.order('created_at', { ascending: false });

    const { data, error } = await query;

    if (error) {
      console.error('❌ Erro na busca:', error);
      throw new Error(error.message);
    }

    console.log('✅ Busca concluída:', data?.length || 0, 'resultados');
    return { success: true, data: data || [] };
  } catch (error) {
    console.error('❌ Erro geral na busca:', error);
    return { success: false, error: error.message };
  }
};

// Contar usuários por status
export const getAdminUsersStats = async () => {
  try {
    console.log('📊 Calculando estatísticas de usuários...');
    
    const { data, error } = await supabase
      .from('profiles')
      .select('is_active')
      .eq('role', 'admin')
      .eq('school_adm', false);  // Filtrar apenas super admins

    if (error) {
      console.error('❌ Erro ao calcular estatísticas:', error);
      throw new Error(error.message);
    }

    const stats = {
      total: data.length,
      active: data.filter(u => u.is_active).length,
      inactive: data.filter(u => !u.is_active).length
    };

    console.log('✅ Estatísticas calculadas:', stats);
    return { success: true, data: stats };
  } catch (error) {
    console.error('❌ Erro geral ao calcular estatísticas:', error);
    return { success: false, error: error.message };
  }
};
